import { z } from 'zod';
import { DisciplineSchema, OfficeSlugSchema } from './common';

export const OfficeSchema = z.object({
  id: z.string(),
  slug: OfficeSlugSchema,
  name: z.string(),
  address: z.string(),
  city: z.string(),
  state: z.string().length(2),
  postalCode: z.string(),
  lat: z.number(),
  lng: z.number(),
  isHeadquarters: z.boolean(),
});
export type Office = z.infer<typeof OfficeSchema>;

// ─── Company DNA — Phase 32 ─────────────────────────────────────────────
//
// Twelve typed sections, persisted in companies.dna_json. Hot-path tables
// (company_top_customers, company_top_manufacturers, company_dna_sync_runs)
// live alongside in D1 — see migration 0039. The DNA blob is what every
// bidding-brain SYSTEM prompt cache-injects, so keep it terse and JSON-stable
// (≤ ~4KB serialized for prompt-cache efficiency).

export const STANCE_VALUES = ['PURSUE', 'STRETCH', 'SKIP'] as const;
export const StanceSchema = z.enum(STANCE_VALUES);
export type Stance = z.infer<typeof StanceSchema>;

export const MFG_TIER_VALUES = ['GOLD', 'SILVER', 'BRONZE', 'STOCKING', 'RESELLER', 'NONE'] as const;
export const MfgTierSchema = z.enum(MFG_TIER_VALUES);
export type MfgTier = z.infer<typeof MfgTierSchema>;

export const DnaIdentitySchema = z.object({
  legalName: z.string().max(200).default(''),
  dbas: z.array(z.string().max(200)).max(10).default([]),
  foundedYear: z.number().int().min(1900).max(2100).nullable().default(null),
  employeeCount: z.object({
    fullTime: z.number().int().nonnegative().default(0),
    partTime: z.number().int().nonnegative().default(0),
    field: z.number().int().nonnegative().default(0),
  }).default({ fullTime: 0, partTime: 0, field: 0 }),
  hqOfficeSlug: OfficeSlugSchema.nullable().default(null),
});
export type DnaIdentity = z.infer<typeof DnaIdentitySchema>;

export const DnaLicensingSchema = z.object({
  cslbNumber: z.string().max(40).default(''),
  cslbClassifications: z.array(z.string().max(20)).max(20).default([]),  // ["C-7","C-10","B"]
  dirRegistrationNumber: z.string().max(40).default(''),
  samUei: z.string().max(40).default(''),
  cageCode: z.string().max(20).default(''),
  duns: z.string().max(20).default(''),
  naicsCodes: z.array(z.string().max(10)).max(20).default([]),
  stateLicenses: z.array(z.object({
    state: z.string().length(2),
    licenseNumber: z.string().max(40),
    expiry: z.string().nullable().default(null),
  })).max(50).default([]),
});
export type DnaLicensing = z.infer<typeof DnaLicensingSchema>;

export const DnaBondingSchema = z.object({
  singleProjectCapacity: z.number().nonnegative().default(0),
  aggregateCapacity: z.number().nonnegative().default(0),
  suretyProvider: z.string().max(120).default(''),
  lastVerifiedAt: z.string().nullable().default(null),
});
export type DnaBonding = z.infer<typeof DnaBondingSchema>;

export const DnaCertificationSchema = z.object({
  name: z.string().max(120),
  issuer: z.string().max(120).default(''),
  expiry: z.string().nullable().default(null),
  scope: z.string().max(200).default(''),
});
export type DnaCertification = z.infer<typeof DnaCertificationSchema>;

export const DnaVerticalSchema = z.object({
  key: z.string().max(40),                 // "transit" | "k12" | "highered" | "healthcare" | "govcon" | "datacenter" | "multifamily" | "industrial" | …
  label: z.string().max(80),
  percentOfRevenue: z.number().min(0).max(100).default(0),
  marqueeCustomers: z.array(z.string().max(120)).max(10).default([]),
  stance: StanceSchema.default('PURSUE'),
  subDisciplines: z.array(DisciplineSchema).default([]),
  notes: z.string().max(800).default(''),
});
export type DnaVertical = z.infer<typeof DnaVerticalSchema>;

export const DnaMfgAuthSchema = z.object({
  manufacturer: z.string().max(120),
  tier: MfgTierSchema.default('NONE'),
  scopeCsiPrefixes: z.array(z.string().max(10)).max(20).default([]),  // ["28 23","27 41"]
  partNumberPrefixes: z.array(z.string().max(20)).max(20).default([]),
  expiry: z.string().nullable().default(null),
  notes: z.string().max(400).default(''),
});
export type DnaMfgAuth = z.infer<typeof DnaMfgAuthSchema>;

export const DnaGeographySchema = z.object({
  serviceRadiusMi: z.number().nonnegative().default(250),
  preferredStates: z.array(z.string().length(2)).max(10).default(['CA', 'NV']),
  outsideBandValueThreshold: z.number().nonnegative().default(2_000_000),  // we'll travel for jobs >= this
  centroid: z.object({ lat: z.number(), lng: z.number() }).nullable().default(null),
});
export type DnaGeography = z.infer<typeof DnaGeographySchema>;

export const DnaCapacitySchema = z.object({
  weeklyEstimatingHours: z.number().nonnegative().default(40),
  fieldCrewByOffice: z.record(OfficeSlugSchema, z.number().int().nonnegative()).default({}),
  maxConcurrentProjects: z.number().int().nonnegative().default(20),
  // currentUtilizationPct is computed live from the projects table, not stored.
});
export type DnaCapacity = z.infer<typeof DnaCapacitySchema>;

export const DnaSweetSpotSchema = z.object({
  optimal: z.object({ min: z.number().nonnegative(), max: z.number().positive() })
    .default({ min: 100_000, max: 1_500_000 }),
  stretch: z.object({ min: z.number().nonnegative(), max: z.number().positive() })
    .default({ min: 1_500_000, max: 5_000_000 }),
  medianRecentValue: z.number().nullable().default(null),
  p25RecentValue: z.number().nullable().default(null),
  p75RecentValue: z.number().nullable().default(null),
});
export type DnaSweetSpot = z.infer<typeof DnaSweetSpotSchema>;

export const DnaIntegrationsSchema = z.object({
  procore: z.object({
    tenantId: z.string().nullable().default(null),
    lastSyncedAt: z.string().nullable().default(null),
    lastSyncStatus: z.enum(['SUCCEEDED', 'FAILED', 'RUNNING']).nullable().default(null),
    lastSyncRunId: z.string().nullable().default(null),
  }).default({ tenantId: null, lastSyncedAt: null, lastSyncStatus: null, lastSyncRunId: null }),
});
export type DnaIntegrations = z.infer<typeof DnaIntegrationsSchema>;

export const DnaStatsSchema = z.object({
  lifetimeProjectCount: z.number().int().nonnegative().default(0),
  lifetimeContractValue: z.number().nonnegative().default(0),
  completedCount: z.number().int().nonnegative().default(0),
  activeCount: z.number().int().nonnegative().default(0),
  inBidCount: z.number().int().nonnegative().default(0),
  inBidValue: z.number().nonnegative().default(0),
  refusedCount: z.number().int().nonnegative().default(0),
  refusedValue: z.number().nonnegative().default(0),
  topStates: z.array(z.object({ state: z.string(), count: z.number().int().nonnegative() })).default([]),
  topProjectTypes: z.array(z.object({ type: z.string(), count: z.number().int().nonnegative() })).default([]),
  lastRecomputedAt: z.string().nullable().default(null),
});
export type DnaStats = z.infer<typeof DnaStatsSchema>;

export const CompanyDnaSchema = z.object({
  identity: DnaIdentitySchema.default({} as DnaIdentity),
  licensing: DnaLicensingSchema.default({} as DnaLicensing),
  bonding: DnaBondingSchema.default({} as DnaBonding),
  certifications: z.array(DnaCertificationSchema).max(50).default([]),
  verticals: z.array(DnaVerticalSchema).max(20).default([]),
  manufacturerAuth: z.array(DnaMfgAuthSchema).max(50).default([]),
  geography: DnaGeographySchema.default({} as DnaGeography),
  capacity: DnaCapacitySchema.default({} as DnaCapacity),
  sweetSpot: DnaSweetSpotSchema.default({} as DnaSweetSpot),
  differentiators: z.array(z.string().max(280)).max(20).default([]),
  integrations: DnaIntegrationsSchema.default({} as DnaIntegrations),
  stats: DnaStatsSchema.default({} as DnaStats),
});
export type CompanyDna = z.infer<typeof CompanyDnaSchema>;

// Each section can be patched independently — typed map of section → partial.
export const COMPANY_DNA_SECTIONS = [
  'identity', 'licensing', 'bonding', 'certifications', 'verticals',
  'manufacturerAuth', 'geography', 'capacity', 'sweetSpot', 'differentiators',
  'integrations', 'stats',
] as const;
export const CompanyDnaSectionSchema = z.enum(COMPANY_DNA_SECTIONS);
export type CompanyDnaSection = z.infer<typeof CompanyDnaSectionSchema>;

// ─── Customer + Manufacturer hot-path rows (D1-backed, not in dna_json) ──

export const TopCustomerSchema = z.object({
  id: z.string(),
  canonicalName: z.string(),
  displayName: z.string(),
  aliases: z.array(z.string()),
  projectCount: z.number().int().nonnegative(),
  totalValue: z.number().nonnegative(),
  completedCount: z.number().int().nonnegative(),
  lastProjectAt: z.string().nullable(),
  procoreProjectIds: z.array(z.number()),
  disciplineMix: z.record(z.string(), z.number()),
  autoPursue: z.boolean(),
  notes: z.string().nullable(),
  source: z.enum(['procore', 'manual']),
});
export type TopCustomer = z.infer<typeof TopCustomerSchema>;

export const UpsertTopCustomerSchema = z.object({
  canonicalName: z.string().min(1).max(200),
  displayName: z.string().min(1).max(200),
  aliases: z.array(z.string()).default([]),
  projectCount: z.number().int().nonnegative().default(0),
  totalValue: z.number().nonnegative().default(0),
  completedCount: z.number().int().nonnegative().default(0),
  lastProjectAt: z.string().nullable().default(null),
  procoreProjectIds: z.array(z.number()).default([]),
  disciplineMix: z.record(z.string(), z.number()).default({}),
  autoPursue: z.boolean().default(false),
  notes: z.string().max(2000).nullable().default(null),
  source: z.enum(['procore', 'manual']).default('manual'),
});
export type UpsertTopCustomer = z.infer<typeof UpsertTopCustomerSchema>;

export const TopManufacturerSchema = z.object({
  id: z.string(),
  canonicalName: z.string(),
  displayName: z.string(),
  csiPrefix: z.string(),
  submittalCount: z.number().int().nonnegative(),
  projectCount: z.number().int().nonnegative(),
  lastUsedAt: z.string().nullable(),
  procoreProjectIds: z.array(z.number()),
});
export type TopManufacturer = z.infer<typeof TopManufacturerSchema>;

// ─── Profile (existing flat fields + DNA blob + computed liveness) ───────

export const CompanyProfileSchema = z.object({
  id: z.string(),
  name: z.string(),
  logoUrl: z.string().nullable(),
  allowedEmailDomain: z.string(),
  disciplines: z.array(DisciplineSchema),
  projectValueMin: z.number().nonnegative(),
  projectValueMax: z.number().positive(),
  offices: z.array(OfficeSchema),
  dna: CompanyDnaSchema,
  dnaExtractedAt: z.string().nullable(),
});
export type CompanyProfile = z.infer<typeof CompanyProfileSchema>;

export const UpdateCompanyProfileSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  disciplines: z.array(DisciplineSchema).min(1).optional(),
  projectValueMin: z.number().nonnegative().optional(),
  projectValueMax: z.number().positive().optional(),
});
export type UpdateCompanyProfile = z.infer<typeof UpdateCompanyProfileSchema>;

// Section-scoped DNA patch. The route validates `section` against
// CompanyDnaSectionSchema, then validates `value` against the matching
// section schema.
export const PatchCompanyDnaSchema = z.object({
  section: CompanyDnaSectionSchema,
  value: z.unknown(),
});
export type PatchCompanyDna = z.infer<typeof PatchCompanyDnaSchema>;

export const SECTION_VALIDATORS: Record<CompanyDnaSection, z.ZodTypeAny> = {
  identity: DnaIdentitySchema,
  licensing: DnaLicensingSchema,
  bonding: DnaBondingSchema,
  certifications: z.array(DnaCertificationSchema).max(50),
  verticals: z.array(DnaVerticalSchema).max(20),
  manufacturerAuth: z.array(DnaMfgAuthSchema).max(50),
  geography: DnaGeographySchema,
  capacity: DnaCapacitySchema,
  sweetSpot: DnaSweetSpotSchema,
  differentiators: z.array(z.string().max(280)).max(20),
  integrations: DnaIntegrationsSchema,
  stats: DnaStatsSchema,
};

// Default DNA — used when companies.dna_json is '{}' (fresh row or pre-32 row).
// Returns a fully-populated CompanyDna with sensible empties so downstream
// code can read fields without null checks.
export function emptyCompanyDna(): CompanyDna {
  return CompanyDnaSchema.parse({});
}
